/**
 * Catalog product text search. Exact substring first, then a compact form so
 * "z 8" still finds "Nikon Z8 Camera" / "Nikon Z 8 Camera", then a barcode
 * comparison that ignores leading zeros, then every meaningful word of the
 * query somewhere on the product.
 *
 * Every text is compared in two forms, and the query matches when any of its
 * forms matches the same form of the product:
 * - the symbol form keeps Unicode as written (case, width and dash look-alikes
 *   unified), so "α6700" finds "α6700" and "Ø67mm" finds "Ø67mm";
 * - the flattened form is plain ASCII (accents stripped, Greek and photo
 *   symbols spelled out: α→a, µ→u, ×→x, ƒ→f, ø→o, é→e), so "a6700" finds
 *   "α6700", "1.4x" finds "1.4×" and "f/2.8" finds "ƒ/2.8".
 * Filler words ("with", "the", "kit") never sink a search on their own,
 * because a product name rarely carries them.
 *
 * Pure, no imports: `frontend/src/utils/catalogSearchMatch.ts` is a byte-identical
 * copy (the admin product browser searches its pre-release set with it), pinned by
 * `catalogSearchMatchTwin.test.ts`. Change both together.
 */

/** Hyphen look-alikes: ‐ ‑ ‒ – — ― − ﹘ ﹣ －. */
const DASHES = /[‐-―−﹘﹣－]/g;
/** What the compact form and the word split ignore. */
const SEPARATORS = /[\s\-_./+&,()]+/g;
/** Words a shopper adds that a product record does not carry. Dropped only when other words remain. */
const FILLER_WORDS = new Set(['with', 'w', 'the', 'and', 'plus', 'kit', 'for']);
/** Digits-only query of a barcode's length: UPC-E / EAN-8 up to GTIN-14. */
const BARCODE_TERM = /^\d{8,14}$/;

/**
 * Characters the flattened form spells in ASCII after NFKD has split accents off (é → e):
 * letters that do not decompose (ø, ß, æ) and the symbols camera listings use. Keys are
 * lower case because the symbol form is lower-cased first (Ø → ø, Ω → ω, Δ → δ).
 */
const FLATTEN: Record<string, string> = {
  // Greek: Sony α bodies, µ (micro) in µm / µs, Ω, Δ, λ.
  'α': 'a', 'β': 'b', 'γ': 'g', 'δ': 'd', 'λ': 'l', 'µ': 'u', 'μ': 'u', 'π': 'p', 'σ': 's', 'ω': 'w',
  // Letters NFKD leaves whole.
  'ø': 'o', 'đ': 'd', 'ł': 'l', 'ß': 'ss', 'æ': 'ae', 'œ': 'oe', 'þ': 'th', 'ı': 'i',
  // Photo and measurement symbols.
  // NFKC has already turned ½ into "1⁄2" and ″ into two ′.
  'ƒ': 'f', '⌀': 'o', '×': 'x', '÷': '/', '⁄': '/', '°': ' deg', '″': '"', '′': "'", '‴': '"',
  // Quotes.
  '‘': "'", '’': "'", '‚': "'", '“': '"', '”': '"', '„': '"',
};
const FLATTEN_PATTERN = new RegExp(`[${Object.keys(FLATTEN).join('')}]`, 'g');

/** Trademark marks: never part of what a product is, and NFKC would turn ™ into the letters "tm". */
const MARKS = /[™®©℠]/g;

/** Symbol form: Unicode kept, trademark marks dropped, case and width folded, every dash a hyphen. */
export function symbolSearchForm(value: string): string {
  return value.replace(MARKS, '').normalize('NFKC').toLowerCase().replace(DASHES, '-');
}

/** Flattened form: the symbol form in plain ASCII, accents stripped and symbols spelled out. */
export function flattenedSearchForm(value: string): string {
  return symbolSearchForm(value)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(FLATTEN_PATTERN, (ch) => FLATTEN[ch])
    .replace(/[^\x20-\x7e]/g, '');
}

/** The distinct search forms of a text: symbol first, then flattened when it differs. */
export function catalogSearchVariants(value: string): string[] {
  const symbol = symbolSearchForm(value);
  const flat = flattenedSearchForm(value);
  return flat === symbol ? [symbol] : [symbol, flat];
}

/** The flattened form, which is what identifier keys (store SKUs, barcodes) compare. */
export const normalizeCatalogSearchText = flattenedSearchForm;

const compact = (value: string): string => value.replace(SEPARATORS, '');
const stripLeadingZeros = (digits: string): string => digits.replace(/^0+/, '');

/** One query form against the product's texts in that same form. */
function matchesForm(texts: string[], term: string): boolean {
  if (texts.some((text) => text.includes(term))) return true;

  const compactTerm = compact(term);
  const compactTexts = texts.map(compact);
  if (compactTerm.length >= 2 && compactTexts.some((text) => text.includes(compactTerm))) return true;

  // A UPC-A typed as an EAN-13 or GTIN-14 (or the reverse) differs only by leading zeros.
  if (BARCODE_TERM.test(compactTerm)) {
    const wanted = stripLeadingZeros(compactTerm);
    if (compactTexts.some((text) => /^\d+$/.test(text) && stripLeadingZeros(text) === wanted)) return true;
  }

  const tokens = term.split(SEPARATORS).filter((token) => token.length > 0);
  const meaningful = tokens.filter((token) => !FILLER_WORDS.has(token));
  const required = meaningful.length > 0 ? meaningful : tokens;
  // One word only: the substring and compact checks above already answered it.
  if (tokens.length < 2 && required.length === tokens.length) return false;
  const haystack = texts.join(' ');
  const compactHaystack = compact(haystack);
  return required.every((token) => haystack.includes(token) || compactHaystack.includes(token));
}

export function catalogTextMatchesSearch(
  fields: Array<string | undefined | null>,
  rawQuery: string
): boolean {
  const raw = String(rawQuery || '').trim();
  if (!raw) return true;

  const present = fields.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  if (present.length === 0) return false;

  // Symbol against symbol keeps "Ø67mm" / "α6700" exact; flattened against flattened
  // bridges a symbol on either side ("a6700" ↔ "α6700", "1.4x" ↔ "1.4×").
  const termSymbol = symbolSearchForm(raw).trim();
  if (termSymbol && matchesForm(present.map(symbolSearchForm), termSymbol)) return true;
  const termFlat = flattenedSearchForm(raw).trim();
  return !!termFlat && matchesForm(present.map(flattenedSearchForm), termFlat);
}
